import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Section, Loading, DataTable, KPITile, StatusPill } from "../components/Common";
import { Vault, Download, Archive } from "lucide-react";
import { toast } from "sonner";

async function openArchive(id) {
  const token = localStorage.getItem("edu360.token");
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/education360/publishing/archive/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) return toast.error("Unable to open PDF");
  window.open(URL.createObjectURL(await res.blob()), "_blank");
}

export default function ParentVault() {
  const [data, setData] = useState(null);
  useEffect(() => { api.parentVault().then(({ data }) => setData(data)).catch(() => setData({ children: [], documents_by_child: {} })); }, []);
  if (!data) return <Loading label="Opening your family vault…" />;

  const totalDocs = Object.values(data.documents_by_child || {}).reduce((a, v) => a + v.length, 0);

  return (
    <div data-testid="parent-vault-page">
      <PageHeader
        kicker="Parent Digital Vault"
        title="Your Family Document Vault"
        subtitle="Every report, invoice, certificate and letter — permanently, securely available to you. Every document is HMAC-signed and independently verifiable via QR."
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <KPITile tone="dark" testId="vault-children" label="Children" value={(data.children || []).length} />
        <KPITile testId="vault-docs" label="Documents" value={totalDocs} />
        <KPITile testId="vault-verified" label="Verifiable" value={totalDocs} accent="via QR" />
        <KPITile tone="dark" testId="vault-permanent" label="Retention" value="Permanent" sub="never deleted" />
      </div>

      {(data.children || []).map((c) => (
        <Section key={c.id} title={`${c.first_name} ${c.last_name}`} subtitle={`${c.year_group} · ${c.house} House · Admission ${c.admission_no}`}>
          <DataTable
            testId={`vault-${c.id}`}
            columns={[
              { key: "subject_label", label: "Document", render: (r) => (
                <div>
                  <div className="font-semibold text-slate-800">{r.subject_label}</div>
                  <div className="text-[11px] text-slate-500 mono">{r.verification_id}</div>
                </div>
              ) },
              { key: "document_type", label: "Type", render: (r) => <span className="capitalize">{(r.document_type || "").replace(/_/g, " ")}</span> },
              { key: "period", label: "Period", render: (r) => `${r.period} · ${r.academic_year}` },
              { key: "version", label: "Version", render: (r) => `v${r.version}` },
              { key: "sha256", label: "Digital Hash", render: (r) => <span className="mono text-[10px] text-slate-400">{(r.sha256 || "").slice(0, 12)}…</span> },
              { key: "created_at", label: "Issued", render: (r) => (r.created_at || "").slice(0, 10) },
              { key: "actions", label: "PDF", render: (r) => (
                <button onClick={() => openArchive(r.id)} className="text-[color:var(--hl-orange)] hover:underline text-xs font-semibold inline-flex items-center gap-1" data-testid={`vault-open-${r.id}`}>
                  <Download className="w-3 h-3" /> Open
                </button>
              ) },
            ]}
            rows={(data.documents_by_child || {})[c.id] || []}
            empty="No documents published yet for this child."
          />
        </Section>
      ))}

      {(data.children || []).length === 0 && (
        <div className="hl-card p-10 text-center text-slate-500">
          <Archive className="w-8 h-8 mx-auto text-slate-300 mb-3" />
          No children linked to this account.
        </div>
      )}
    </div>
  );
}
